import {
  Grid,
  Heading,
  Skeleton,
  CardBody,
  Card,
  Button,
  Flex,
  VStack,
  Image,
  Avatar,
} from '@chakra-ui/react';
import range from 'lodash/range';
import { memo, useState } from 'react';
import { T } from '@transifex/react';
import { env, hostnames } from 'features/common/config/envConfig';
import { useGetCustomAppTenantsQuery } from 'gql/graphql';
import TruncatedText from 'features/common/components/TruncatedText';
import NoTenantsPanelMessage from './NoTenantsPanelMessage';

export const systemIds = {
  [hostnames.internal]: 'cf6dff19-052b-4482-8200-7dea7f4bd04e',
  [hostnames.localhost]: 'cf6dff19-052b-4482-8200-7dea7f4bd04e',
  [hostnames.dev]: 'cf6dff19-052b-4482-8200-7dea7f4bd04e',
  [hostnames.qa]: 'cf6dff19-052b-4482-8200-7dea7f4bd04e',
  [hostnames.staging]: 'cf6dff19-052b-4482-8200-7dea7f4bd04e',
  [hostnames.beta]: 'a179aa11-40a9-45ce-b8c7-a8722a916691',
  [hostnames['pre-production']]: 'a179aa11-40a9-45ce-b8c7-a8722a916691',
  [hostnames.production]: '5c01153f-464a-464d-b691-23c131bf73f0',
};

const MarketplaceAppsPanelContent = () => {
  const [failedImages, setFailedImages] = useState<Set<string>>(new Set());

  const {
    data: tenantsQueryData,
    loading: tenantsQueryLoading,
    fetchMore,
  } = useGetCustomAppTenantsQuery({
    variables: {
      first: 20,
      env,
      systemId: systemIds[window.location.hostname],
      state: 'Active',
    },
    notifyOnNetworkStatusChange: true,
    nextFetchPolicy: 'cache-only',
  });

  const edges = tenantsQueryData?.user?.tenants?.edges ?? [];
  const nodes = tenantsQueryData?.user?.tenants?.nodes ?? [];

  // Function to get the app icon URL from annotations
  const getIconUrl = (node: any): string => {
    const iconAnnotation = node?.annotations?.find(
      (annotation: any) => annotation.key === 'IconUrl',
    );
    return iconAnnotation?.value;
  };

  // Function to get the app URL from annotations
  const getAppUrl = (node: any): string => {
    const urlAnnotation = node?.annotations?.find(
      (annotation: any) => annotation.key === 'URL',
    );
    return urlAnnotation?.value || '#';
  };

  // Function to handle app click - opens in a new tab
  const handleAppClick = (url: string) => {
    if (url && url !== '#') {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  // Function to handle image load error
  const handleImageError = (nodeId: string) => {
    setFailedImages((prev) => {
      const newSet = new Set([...prev, nodeId]);
      return newSet;
    });
  };

  // Function to check if image should show fallback
  const shouldShowFallback = (node: any): boolean => {
    const iconUrl = getIconUrl(node);
    const hasFailed = failedImages.has(node.id);
    const hasNoIcon = !iconUrl || iconUrl.trim() === '';

    return hasFailed || hasNoIcon;
  };

  const fetchMoreTenants = () => {
    const { endCursor } = tenantsQueryData?.user?.tenants?.pageInfo || {};

    return fetchMore({
      variables: {
        after: endCursor,
      },
      updateQuery: (prev: any, { fetchMoreResult }) => {
        if (!fetchMoreResult) return prev;

        const pageInfo = fetchMoreResult?.user?.tenants?.pageInfo || {
          hasNextPage: false,
          hasPreviousPage: false,
          startCursor: null,
          endCursor: null,
        };

        return {
          user: {
            ...fetchMoreResult.user,
            tenants: {
              ...fetchMoreResult.user?.tenants,
              edges: [
                ...(prev?.user?.tenants?.edges ?? []),
                ...(fetchMoreResult?.user?.tenants?.edges ?? []),
              ],
              nodes: [
                ...(prev?.user?.tenants?.nodes ?? []),
                ...(fetchMoreResult?.user?.tenants?.nodes ?? []),
              ],
              pageInfo,
              totalCount: fetchMoreResult?.user?.tenants?.totalCount || 0,
            },
          },
        };
      },
    });
  };

  const shouldShowMoreButton =
    (!tenantsQueryLoading &&
      tenantsQueryData?.user?.tenants?.pageInfo?.hasNextPage) ??
    false;

  return (
    <>
      <Heading size='md' noOfLines={1}>
        Marketplace apps
      </Heading>
      <Card
        variant='flat'
        size={['md', 'lg']}
        w='full'
        data-testid='marketplace-apps-panel-block'
        data-behavior-analytics-feature='marketplace-apps-panel - home page'
      >
        <CardBody>
          {tenantsQueryLoading ? (
            <Grid
              gap='4'
              templateColumns='repeat(auto-fill, minmax(var(--sizes-40), 1fr))'
            >
              {range(5).map((_, index) => (
                <Skeleton
                  h='32'
                  w='40'
                  isLoaded={false}
                  key={index}
                  data-testid='marketplace-apps-panel-skeleton-placeholder'
                />
              ))}
            </Grid>
          ) : edges.length < 1 ? (
            <NoTenantsPanelMessage />
          ) : (
            <Grid
              columnGap='4'
              rowGap='4'
              templateColumns={[
                'repeat(auto-fill, minmax(var(--sizes-32), 1fr))',
                'repeat(auto-fill, minmax(var(--sizes-44), 1fr))',
              ]}
            >
              {nodes.map((node) => (
                <VStack
                  key={node.id}
                  p='4'
                  onClick={() => handleAppClick(getAppUrl(node))}
                  cursor='pointer'
                  _hover={{ backgroundColor: 'neutral.100' }}
                  _active={{ backgroundColor: 'neutral.200' }}
                  borderRadius='md'
                  transitionProperty='common'
                  transitionDuration='normal'
                  data-testid={`marketplace-app-tile-${node.id}`}
                >
                  {shouldShowFallback(node) ? (
                    <Avatar
                      size='lg'
                      name={node.displayName}
                      getInitials={(name) => name.charAt(0).toUpperCase()}
                      borderRadius='lg'
                    />
                  ) : (
                    <Image
                      boxSize='16'
                      src={getIconUrl(node)}
                      alt={node.displayName}
                      onError={() => handleImageError(node.id)}
                    />
                  )}
                  <TruncatedText text={node.displayName} maxLength={20} />
                </VStack>
              ))}
            </Grid>
          )}
          {shouldShowMoreButton && (
            <Flex align='center' justifyContent='center'>
              <Button
                variant='link'
                mt='6'
                onClick={fetchMoreTenants}
                data-testid='marketplace-apps-show-more-button'
              >
                <T _str='Show more' />
              </Button>
            </Flex>
          )}
        </CardBody>
      </Card>
    </>
  );
};

export const MarketplaceAppsPanel = memo(MarketplaceAppsPanelContent);
